import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Share,
  Copy,
  Check,
  Twitter,
  Linkedin,
  Facebook,
  Download,
  Loader,
} from "lucide-react";
import html2canvas from "html2canvas";
import { toast } from "@/hooks/use-toast";

interface ShareButtonProps {
  title?: string;
  description?: string;
  url?: string;
  elementId?: string;
}

export function ShareButton({
  title = "Share this transaction",
  description,
  url,
  elementId,
}: ShareButtonProps) {
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState({
    title: "",
    description: "",
  });
  const [copied, setCopied] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const shareUrl = url || window.location.href;
  const shareTitle = title || "Check out this carbon credit transaction";
  const shareDescription =
    description || "I just made a positive impact on the environment!";

  const handleCopy = () => {
    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      setAlertMessage({
        title: "Link copied",
        description:
          "The link has been copied to your clipboard. You can now share it with others.",
      });
      setIsAlertOpen(true);
    });
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareDescription,
          url: shareUrl,
        });
      } catch (error) {
        console.error("Error sharing:", error);
      }
    } else {
      handleCopy();
    }
  };

  const openShareWindow = (url: string) => {
    window.open(
      url,
      "share-dialog",
      "width=600,height=400,location=no,menubar=no,toolbar=no",
    );
  };

  const shareToTwitter = () => {
    openShareWindow(
      `https://twitter.com/intent/tweet?url=${encodeURIComponent(
        shareUrl,
      )}&text=${encodeURIComponent(shareTitle)}`,
    );
  };

  const shareToLinkedIn = () => {
    openShareWindow(
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
        shareUrl,
      )}`,
    );
  };

  const shareToFacebook = () => {
    openShareWindow(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
        shareUrl,
      )}`,
    );
  };

  const downloadAsImage = async () => {
    if (!elementId) {
      console.error("No element ID provided for download");
      toast({
        title: "Download Failed",
        description:
          "Unable to generate the confirmation image. Please try again.",
        variant: "destructive",
      });
      return;
    }

    const element = document.getElementById(elementId);
    if (!element) {
      console.error("Element not found:", elementId);
      toast({
        title: "Download Failed",
        description:
          "Unable to generate the confirmation image. Please try again.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsDownloading(true);

      // Find the toggle button in the real DOM
      const toggleButton = element.querySelector(
        "#retirements-toggle-button",
      ) as HTMLElement | null;

      const shouldExpand =
        toggleButton.getAttribute("aria-expanded") === "false";
      // If the toggle button exists, click it to expand the content
      if (toggleButton && shouldExpand) {
        toggleButton.click();
        // Wait a moment for React to re-render and the DOM to update
        await new Promise((resolve) => {
          const checkCondition = setInterval(() => {
            if (
              toggleButton.getAttribute("aria-expanded") === "true" &&
              toggleButton.getAttribute("aria-loading") === "false"
            ) {
              clearInterval(checkCondition);
              resolve(true);
            }
          }, 1);
        });
      }

      const clonedElement = element.cloneNode(true) as HTMLElement;

      // If we toggled the button, click it again on the original element to restore the UI
      if (toggleButton && shouldExpand) {
        toggleButton.click();
      }

      clonedElement.style.position = "absolute";
      clonedElement.style.top = "-9999px";
      clonedElement.style.left = "-9999px";
      clonedElement.style.maxWidth = "800px";
      clonedElement.style.width = "100%";
      clonedElement.style.margin = "0";
      clonedElement.style.borderRadius = "0";
      clonedElement.style.transform = "none";
      clonedElement.style.boxShadow = "none";

      // Remove account information
      clonedElement.querySelector("#account-information-container")?.remove();

      // Remove the toggle button from the clone
      clonedElement.querySelector("#retirements-toggle-button")?.remove();

      // Remove issuer logo and align issuer name to the left
      const issuerSections = clonedElement.querySelectorAll(".share-exclude");
      issuerSections.forEach((section) => {
        const avatar = section.querySelector(".h-10.w-10.mr-2");
        if (avatar && avatar.parentNode) {
          avatar.parentNode.removeChild(avatar);
        }
        const issuerName = section.querySelector(".font-medium.text-gray-900");
        if (issuerName) {
          (issuerName as HTMLElement).style.textAlign = "left";
          (issuerName as HTMLElement).style.display = "block";
        }
      });

      // Continue with existing code for cropped IDs
      const croppedIdComponents =
        clonedElement.querySelectorAll(".flex.items-center");
      croppedIdComponents.forEach((component) => {
        const tooltipTrigger = component.querySelector("[data-full-id]");
        if (tooltipTrigger) {
          const fullId = tooltipTrigger.getAttribute("data-full-id");
          if (fullId) {
            const fullIdElement = document.createElement("span");
            fullIdElement.textContent = fullId;
            fullIdElement.style.fontFamily = "monospace";
            fullIdElement.style.fontSize = "0.75rem";
            fullIdElement.style.fontWeight = "bold";
            fullIdElement.style.wordBreak = "break-all";
            fullIdElement.style.whiteSpace = "normal";
            fullIdElement.style.maxWidth = "100%";
            fullIdElement.style.display = "inline-block";

            const parentContainer = component.parentElement;
            if (parentContainer) {
              const labelElement = parentContainer.querySelector(
                ".text-sm.text-gray-500, .text-sm.text-gray-600, .text-xs.text-gray-500, .text-xs.text-muted-foreground",
              );
              if (labelElement) {
                const container = document.createElement("div");
                container.style.margin = "0";
                container.style.padding = "0";
                container.appendChild(labelElement.cloneNode(true));
                container.appendChild(document.createTextNode(" "));
                container.appendChild(fullIdElement);
                parentContainer.replaceChild(container, component);
              } else {
                parentContainer.replaceChild(fullIdElement, component);
              }
            }
          }
        }
      });

      const remainingTooltipTriggers =
        clonedElement.querySelectorAll("[data-full-id]");
      remainingTooltipTriggers.forEach((element) => {
        const fullId = element.getAttribute("data-full-id");
        if (fullId) {
          element.textContent = fullId;
          (element as HTMLElement).style.fontSize = "0.75rem";
          (element as HTMLElement).style.fontFamily = "monospace";
          (element as HTMLElement).style.fontWeight = "bold";
          (element as HTMLElement).style.wordBreak = "break-all";
          (element as HTMLElement).style.whiteSpace = "normal";
          (element as HTMLElement).style.maxWidth = "100%";
          (element as HTMLElement).style.display = "inline-block";
        }
      });

      const copyButtons = clonedElement.querySelectorAll("button");
      copyButtons.forEach((button) => {
        if (button.parentNode) {
          button.parentNode.removeChild(button);
        }
      });

      const buttonsToRemove = clonedElement.querySelectorAll(
        'button, a.button, [role="button"], .card-footer, footer',
      );
      buttonsToRemove.forEach((button) => {
        if (button.parentNode) {
          button.parentNode.removeChild(button);
        }
      });

      const footers = clonedElement.querySelectorAll(
        '[class*="card-footer"], [class*="CardFooter"]',
      );
      footers.forEach((footer) => {
        if (footer.parentNode) {
          footer.parentNode.removeChild(footer);
        }
      });

      const imageColumns = clonedElement.querySelectorAll(
        ".md\\:col-span-1:not(.md\\:col-span-2)",
      );
      imageColumns.forEach((column) => {
        if (column.parentNode) {
          column.parentNode.removeChild(column);
        }
      });

      const imageContainers = clonedElement.querySelectorAll(
        "div.flex.flex-col.h-full",
      );
      imageContainers.forEach((container) => {
        if (container.parentNode) {
          container.parentNode.removeChild(container);
        }
      });

      const projectLinks = clonedElement.querySelectorAll(
        'a[href*="/projects/"]',
      );
      projectLinks.forEach((link) => {
        const span = document.createElement("span");
        span.className = "font-medium text-gray-900";
        span.textContent = link.textContent;
        if (link.parentNode) {
          link.parentNode.replaceChild(span, link);
        }
      });

      const externalLinkIcons = clonedElement.querySelectorAll(
        ".lucide-external-link",
      );
      externalLinkIcons.forEach((icon) => {
        if (icon.parentNode) {
          icon.parentNode.removeChild(icon);
        }
      });

      const contentColumns = clonedElement.querySelectorAll(".md\\:col-span-2");
      contentColumns.forEach((column) => {
        column.classList.remove("md:col-span-2");
        (column as HTMLElement).style.width = "100%";
      });

      const gridContainers = clonedElement.querySelectorAll(
        ".grid.grid-cols-1.md\\:grid-cols-3",
      );
      gridContainers.forEach((container) => {
        container.classList.remove("md:grid-cols-3");
        (container as HTMLElement).style.display = "block";
        (container as HTMLElement).style.width = "100%";
      });

      const accountInfoSection = clonedElement.querySelectorAll(
        ".grid.grid-cols-1.md\\:grid-cols-2",
      );
      accountInfoSection.forEach((section) => {
        section.classList.remove("md:grid-cols-2");
        (section as HTMLElement).style.display = "block";

        const accountBoxes = section.querySelectorAll(
          ".bg-secondary.p-3.rounded-md",
        );
        accountBoxes.forEach((box, index) => {
          if (index > 0) {
            (box as HTMLElement).style.marginTop = "16px";
          }

          (box as HTMLElement).style.width = "100%";

          const truncatedTexts = box.querySelectorAll(".truncate");
          truncatedTexts.forEach((text) => {
            text.classList.remove("truncate");
            (text as HTMLElement).style.wordBreak = "break-word";
            (text as HTMLElement).style.whiteSpace = "normal";
            (text as HTMLElement).style.overflow = "visible";
          });

          const accountDetails = box.querySelectorAll(".flex.items-center");
          accountDetails.forEach((detail) => {
            (detail as HTMLElement).style.display = "block";
            (detail as HTMLElement).style.marginBottom = "4px";

            const label = detail.querySelector(
              ".text-muted-foreground",
            ) as HTMLElement;
            if (label) {
              const idElement = detail.querySelector("[data-full-id]");
              if (idElement) {
                label.textContent = "ID:";
              } else {
                label.textContent = "Type:";
              }

              label.style.width = "auto";
              label.style.marginRight = "4px";
              label.style.display = "inline";
            }
          });
        });
      });

      document.body.appendChild(clonedElement);

      const canvas = await html2canvas(clonedElement, {
        logging: false,
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
        scale: 2,
      });

      document.body.removeChild(clonedElement);

      const dataUrl = canvas.toDataURL("image/png");
      const link = document.createElement("a");
      link.download = "transaction-confirmation.png";
      link.href = dataUrl;
      link.click();

      toast({
        title: "Download Complete",
        description:
          "Your transaction confirmation has been downloaded as an image.",
      });
    } catch (error) {
      console.error("Error generating image:", error);
      toast({
        title: "Download Failed",
        description:
          "Unable to generate the confirmation image. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="flex flex-wrap gap-2">
      {navigator.share ? (
        <Button onClick={handleShare} variant="outline" className="gap-2">
          <Share className="h-4 w-4" />
          Share
        </Button>
      ) : (
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="gap-2">
              <Share className="h-4 w-4" />
              Share
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-2">
            <div className="flex flex-col gap-2 min-w-[200px]">
              <Button
                variant="outline"
                size="sm"
                className="justify-start gap-2"
                onClick={handleCopy}
              >
                {copied ? (
                  <Check className="h-4 w-4" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
                Copy link
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="justify-start gap-2"
                onClick={downloadAsImage}
                disabled={isDownloading}
              >
                {isDownloading ? (
                  <Loader className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                Download as Image
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="justify-start gap-2"
                onClick={shareToTwitter}
              >
                <Twitter className="h-4 w-4" />
                Share on Twitter
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="justify-start gap-2"
                onClick={shareToLinkedIn}
              >
                <Linkedin className="h-4 w-4" />
                Share on LinkedIn
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="justify-start gap-2"
                onClick={shareToFacebook}
              >
                <Facebook className="h-4 w-4" />
                Share on Facebook
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      )}

      <Button
        variant="outline"
        className="gap-2"
        onClick={downloadAsImage}
        disabled={isDownloading}
      >
        {isDownloading ? (
          <Loader className="h-4 w-4 animate-spin" />
        ) : (
          <Download className="h-4 w-4" />
        )}
        Download
      </Button>
      <AlertDialog open={isAlertOpen} onOpenChange={setIsAlertOpen}>
        <AlertDialogContent>
          <AlertDialogTitle>{alertMessage.title}</AlertDialogTitle>
          <AlertDialogDescription>
            {alertMessage.description}
          </AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogAction>OK</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
